import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { ArrowUpDown, Printer, FileDown } from 'lucide-react';
import DateRangeFilter from './DateRangeFilter';
import { exportCSV, inRange, loadsForUnit } from './reportUtils';
import { useCompanyInfo, generatePrintHTML, openPrint } from './ReportExporter';
import { computeLoadGross } from '@/lib/invoiceHelpers';

// Revenue minus true operating cost per unit: fuel + maintenance actually
// incurred in the period, plus weekly fixed costs (insurance, ELD, truck
// and trailer payments -- the same figures SettlementReviewModal deducts
// from driver pay) prorated across the number of weeks in the selected
// range. Weekly fixed costs are skipped entirely when no date range is
// set, since there's no week count to prorate them against.
export default function UnitOperatingIncomeReport() {
  const [loads, setLoads] = useState([]);
  const [fuelExpenses, setFuelExpenses] = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [assets, setAssets] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [companySettings, setCompanySettings] = useState([]);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState('Net Operating Income');
  const [sortDir, setSortDir] = useState(-1);
  const company = useCompanyInfo();

  useEffect(() => {
    Promise.all([
      client.entities.Load.list('-delivery_date', 500),
      client.entities.Expense.filter({ category: 'Fuel' }, '-date', 1000),
      client.entities.MaintenanceRecord.list('-service_date', 500),
      client.entities.Asset.filter({ asset_type: 'Truck' }, 'unit_number', 200),
      client.entities.DriverProfile.list('full_name', 100),
      client.entities.CompanySettings.list(),
    ]).then(([l, f, m, a, d, cs]) => {
      setLoads(l); setFuelExpenses(f); setMaintenance(m); setAssets(a); setDrivers(d); setCompanySettings(cs);
      setLoading(false);
    });
  }, []);

  const insuranceWeekly = parseFloat(companySettings.find(s => s.key === 'insurance_deduction')?.value) || 0;
  const eldWeekly = parseFloat(companySettings.find(s => s.key === 'eld_weekly_cost')?.value) || 0;

  const weeks = (start && end)
    ? Math.max(1, (new Date(end) - new Date(start)) / (1000 * 60 * 60 * 24 * 7))
    : 0;

  const filteredLoads = loads.filter(l => inRange(l.delivery_date || l.pickup_date, start, end) && l.assigned_driver_email);
  const filteredFuel = fuelExpenses.filter(e => inRange(e.date, start, end));
  const filteredMaint = maintenance.filter(m => inRange(m.service_date, start, end));

  let rows = assets.filter(a => a.assigned_driver_email).map(a => {
    const driver = drivers.find(d => d.user_email === a.assigned_driver_email);
    // Was `l.rate || 0` grouped by "whichever driver is CURRENTLY on this
    // unit" -- missed accessorial fees (same gap fixed in
    // UnitRevenueReport.jsx) and misattributed historical loads once a
    // driver is reassigned to a different truck (loadsForUnit prefers each
    // load's own truck_unit snapshot instead, see reportUtils.jsx).
    const revenue = loadsForUnit(filteredLoads, a).reduce((s, l) => s + computeLoadGross(l), 0);
    const fuel = filteredFuel.filter(e => e.driver_email === a.assigned_driver_email).reduce((s, e) => s + (e.amount || 0), 0);
    const maint = filteredMaint.filter(m => m.unit_number === a.unit_number).reduce((s, m) => s + (m.cost || 0), 0);
    const weeklyFixed = insuranceWeekly + eldWeekly + (driver?.weekly_truck_payment || 0) + (driver?.weekly_trailer_payment || 0);
    const fixedCost = weeklyFixed * weeks;
    const totalCost = fuel + maint + fixedCost;
    return {
      'Unit #': a.unit_number,
      Driver: driver?.full_name || a.assigned_driver_email || '—',
      Revenue: revenue,
      Fuel: fuel,
      Maintenance: maint,
      'Fixed Costs': fixedCost,
      'Net Operating Income': revenue - totalCost,
    };
  }).filter(r => r.Revenue > 0 || r.Fuel > 0 || r.Maintenance > 0);

  rows.sort((a, b) => sortDir * ((b[sortKey] ?? 0) - (a[sortKey] ?? 0)));

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d * -1);
    else { setSortKey(key); setSortDir(-1); }
  };

  const grandRevenue = rows.reduce((s, r) => s + r.Revenue, 0);
  const grandNOI = rows.reduce((s, r) => s + r['Net Operating Income'], 0);

  const csvRows = rows.map(r => ({ ...r, Revenue: r.Revenue.toFixed(2), Fuel: r.Fuel.toFixed(2), Maintenance: r.Maintenance.toFixed(2), 'Fixed Costs': r['Fixed Costs'].toFixed(2), 'Net Operating Income': r['Net Operating Income'].toFixed(2) }));

  const handlePrint = () => {
    const cols = ['Unit #', 'Driver', 'Revenue', 'Fuel', 'Maintenance', 'Fixed Costs', 'Net Operating Income'];
    const dataRows = rows.map(r => [
      { value: r['Unit #'], class: 'bold' }, r.Driver,
      { value: `$${r.Revenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, class: 'positive' },
      { value: `-$${r.Fuel.toFixed(2)}`, class: 'negative' },
      { value: `-$${r.Maintenance.toFixed(2)}`, class: 'negative' },
      { value: `-$${r['Fixed Costs'].toFixed(2)}`, class: 'negative' },
      { value: `$${r['Net Operating Income'].toFixed(2)}`, class: r['Net Operating Income'] >= 0 ? 'positive' : 'negative' },
    ]);
    const totalsRow = ['TOTALS', '', { value: `$${grandRevenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, class: 'gold' }, '', '', '', { value: `$${grandNOI.toFixed(2)}`, class: 'gold' }];
    const cards = [
      { label: 'Units', value: rows.length, color: 'gold' },
      { label: 'Total Revenue', value: `$${grandRevenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, color: 'positive' },
      { label: 'Net Operating Income', value: `$${grandNOI.toFixed(2)}`, color: grandNOI >= 0 ? 'positive' : 'negative' },
    ];
    const html = generatePrintHTML('Unit Operating Income Report', `${start || 'All'} – ${end || 'All'}`, cols, dataRows, totalsRow, company, cards);
    openPrint(html);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <DateRangeFilter onChange={(s, e) => { setStart(s); setEnd(e); }} onExport={() => exportCSV(csvRows, 'unit-operating-income.csv')} exportDisabled={loading || rows.length === 0} />
        <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><Printer className="w-4 h-4" /> Print</button>
        <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><FileDown className="w-4 h-4" /> Export PDF</button>
      </div>
      <div className="glass-card overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border">
              {['Unit #', 'Driver', 'Revenue', 'Fuel', 'Maintenance', 'Fixed Costs', 'Net Operating Income'].map(h => (
                <th key={h} onClick={() => toggleSort(h)} className="text-left px-3 py-2.5 text-muted-foreground font-medium whitespace-nowrap cursor-pointer hover:text-foreground select-none">
                  <span className="flex items-center gap-1">{h} <ArrowUpDown className="w-3 h-3 opacity-50" /></span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map(r => (
              <tr key={r['Unit #']} className="hover:bg-white/3">
                <td className="px-3 py-2.5 font-semibold text-primary">{r['Unit #']}</td>
                <td className="px-3 py-2.5 text-foreground">{r.Driver}</td>
                <td className="px-3 py-2.5 font-semibold text-green-400">${r.Revenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                <td className="px-3 py-2.5 text-red-400">−${r.Fuel.toFixed(2)}</td>
                <td className="px-3 py-2.5 text-red-400">−${r.Maintenance.toFixed(2)}</td>
                <td className="px-3 py-2.5 text-amber-400">−${r['Fixed Costs'].toFixed(2)}</td>
                <td className={`px-3 py-2.5 font-bold ${r['Net Operating Income'] >= 0 ? 'text-green-400' : 'text-red-400'}`}>${r['Net Operating Income'].toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="border-t border-border bg-white/5 font-bold">
                <td colSpan={2} className="px-3 py-2.5 text-foreground">TOTALS</td>
                <td className="px-3 py-2.5 text-foreground">${grandRevenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                <td colSpan={3}></td>
                <td className={`px-3 py-2.5 ${grandNOI >= 0 ? 'text-green-400' : 'text-red-400'}`}>${grandNOI.toFixed(2)}</td>
              </tr>
            </tfoot>
          )}
        </table>
        {rows.length === 0 && !loading && <p className="text-center text-muted-foreground py-8 text-sm">No unit data for this period.</p>}
        {loading && <p className="text-center text-muted-foreground py-8 text-sm">Loading...</p>}
      </div>
      <p className="text-xs text-muted-foreground">
        Fixed Costs = (insurance + ELD + truck payment + trailer payment) × weeks in range{weeks > 0 ? ` (${weeks.toFixed(1)} weeks)` : ' -- set a date range to include fixed costs'}. Same per-driver figures used in driver settlements.
      </p>
    </div>
  );
}
